import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Autocomplete,
  Button,
  Collapse,
  Popover,
  Divider,
  Chip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { parse, isValid } from "date-fns";
import API from "../../utils/Axios.utils";
import { Notification } from "../../utils/Notification";
import InterviewEvaluationModal from "./InterviewEvaluationModal";

const ROUND_LABELS = {
  L1_Round: "L1 Technical",
  L2_Round: "L2 Architecture",
  L3_Round: "L3 Final",
};

const ROW_HEIGHT_PX = 48;
const BUSINESS_HOUR_START = 8; // default scroll position when the grid opens
const DURATION_OPTIONS = [15, 30, 45, 60, 90];
const DEFAULT_DURATION_MIN = 45; // cosmetic only — not persisted to the backend

const HOUR_SLOTS = Array.from({ length: 24 }, (_, i) => i); // 12 AM – 11 PM, full scrollable day

const formatHour = (h) => {
  const suffix = h >= 12 ? "PM" : "AM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display} ${suffix}`;
};

const pad2 = (n) => String(n).padStart(2, "0");

const getWeekDays = (baseDate) => {
  const d = new Date(baseDate);
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    return dd;
  });
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Robust date parsing supporting standard ISO-8601 strings and legacy 'yyyy-MM-dd hh:mm a' formats
const parseInterviewDateTime = (str) => {
  if (!str) return null;
  // If standard ISO string (e.g. 2026-10-05T09:00:00.000Z or 2026-10-05T09:00)
  if (typeof str === "string" && str.includes("T")) {
    const isoDate = new Date(str);
    if (isValid(isoDate) && !isNaN(isoDate.getTime())) return isoDate;
  }
  // Try date-fns parse for 12-hour formatted string
  const d = parse(str, "yyyy-MM-dd hh:mm a", new Date());
  if (isValid(d)) return d;
  // Fallback to native date
  const fallback = new Date(str);
  return isValid(fallback) && !isNaN(fallback.getTime()) ? fallback : null;
};

const isValidUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  try {
    const u = new URL(value.trim());
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    if (!u.hostname.includes(".") || u.hostname.startsWith(".") || u.hostname.endsWith(".")) return false;
    return true;
  } catch {
    return false;
  }
};

// Shared form used by SchedulingCard (new, sidebar-driven), the calendar edit Popover
// (existing event), and the calendar empty-cell-click Popover (new, candidate picker shown).
const InterviewForm = ({
  initialDate = "",
  initialTime = "",
  initialLink = "",
  profileId = null,
  candidates = null,
  isFeedbackSubmitted = false,
  evaluationDecision = null,
  feedbackText = "",
  onSave,
  onDelete,
  onEvaluate,
  onError,
}) => {
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [duration, setDuration] = useState(DEFAULT_DURATION_MIN);
  const [link, setLink] = useState(initialLink);
  const [selectedCandidateId, setSelectedCandidateId] = useState(profileId);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const now = new Date();
  const todayStr = now.toISOString().split("T")[0];
  const currentTimeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const isToday = date === todayStr;
  const isPast = date !== "" && time !== "" && new Date(`${date}T${time}`) <= now;

  const isLinkValid = link.trim() !== "" && isValidUrl(link);
  const hasCandidate = candidates ? selectedCandidateId !== null : true;
  const canSave = date !== "" && time !== "" && isLinkValid && !isPast && hasCandidate;
  const busy = saving || deleting;

  const handleSave = async () => {
    setSaving(true);
    try {
      const resolvedId = candidates ? selectedCandidateId : profileId;
      await API.put(`api/interview/scheduling/${resolvedId}`, {
        interview_datetime: `${date}T${time}`,
        interview_link: link,
      });
      onSave(resolvedId, duration);
    } catch (err) {
      onError?.(err?.response?.data?.message || err?.message || "Failed to schedule interview.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`api/interview/scheduling/${profileId}`);
      onDelete();
    } catch (err) {
      onError?.(err?.response?.data?.message || err?.message || "Failed to remove interview schedule.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {candidates && (
        candidates.length > 0 ? (
          <Autocomplete
            size="small"
            options={candidates}
            disabled={busy}
            getOptionLabel={(c) => c.candidate_name}
            isOptionEqualToValue={(a, b) => a.profile_id === b.profile_id}
            onChange={(_, val) => setSelectedCandidateId(val?.profile_id ?? null)}
            renderInput={(params) => <TextField {...params} label="Candidate" size="small" />}
          />
        ) : (
          <Typography variant="caption" color="text.secondary" textAlign="center" py={1}>
            No candidates awaiting scheduling
          </Typography>
        )
      )}
      <TextField
        label="Interview Date"
        type="date"
        size="small"
        fullWidth
        value={date}
        onChange={(e) => setDate(e.target.value)}
        disabled={busy}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: todayStr }}
      />
      <TextField
        label="Interview Time (IST)"
        type="time"
        size="small"
        fullWidth
        value={time}
        onChange={(e) => setTime(e.target.value)}
        disabled={busy}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: isToday ? currentTimeStr : undefined }}
        error={isPast}
        helperText={isPast ? "Cannot schedule in the past" : undefined}
      />
      <TextField
        select
        label="Duration"
        size="small"
        fullWidth
        value={duration}
        onChange={(e) => setDuration(Number(e.target.value))}
        disabled={busy}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              onWheel: (e) => e.stopPropagation(),
              style: { maxHeight: 250 },
            },
          },
        }}
      >
        {DURATION_OPTIONS.map((m) => (
          <MenuItem key={m} value={m}>{m} min</MenuItem>
        ))}
      </TextField>
      <TextField
        label="Meeting Link"
        type="url"
        size="small"
        fullWidth
        placeholder="https://…"
        value={link}
        onChange={(e) => setLink(e.target.value)}
        disabled={busy}
        error={link.trim() !== "" && !isLinkValid}
        helperText={link.trim() !== "" && !isLinkValid ? "Enter a valid http(s) URL" : undefined}
      />
      <Button
        variant="contained"
        size="small"
        fullWidth
        disabled={!canSave || busy}
        onClick={handleSave}
        sx={{ textTransform: "none" }}
      >
        {saving ? <CircularProgress size={14} color="inherit" /> : "Schedule"}
      </Button>
      {/* Feedback Section */}
      {isFeedbackSubmitted ? (
        <Box sx={{ mt: 1, p: 1.25, bgcolor: evaluationDecision === "passed" ? "#ECFDF5" : "#FEF2F2", borderRadius: 1.5, border: "1px solid", borderColor: evaluationDecision === "passed" ? "#A7F3D0" : "#FECACA" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" fontWeight={700} color={evaluationDecision === "passed" ? "#065F46" : "#991B1B"}>
              {evaluationDecision === "passed" ? "✓ Feedback: Passed" : "✕ Feedback: Failed"}
            </Typography>
            <Chip
              label="Submitted"
              size="small"
              color={evaluationDecision === "passed" ? "success" : "error"}
              sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
            />
          </Box>
          {feedbackText && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: "italic", maxHeight: 36, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              "{feedbackText}"
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={onEvaluate}
            startIcon={<RateReviewIcon fontSize="small" />}
            sx={{
              mt: 0.75,
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              borderColor: evaluationDecision === "passed" ? "#10B981" : "#EF4444",
              color: evaluationDecision === "passed" ? "#065F46" : "#991B1B",
              "&:hover": {
                borderColor: evaluationDecision === "passed" ? "#059669" : "#DC2626",
                bgcolor: evaluationDecision === "passed" ? "#D1FAE5" : "#FEE2E2",
              },
            }}
          >
            View / Update Feedback
          </Button>
        </Box>
      ) : (
        onEvaluate && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Button
              variant="contained"
              size="small"
              fullWidth
              disabled={busy}
              onClick={onEvaluate}
              startIcon={<RateReviewIcon fontSize="small" />}
              sx={{
                textTransform: "none",
                backgroundColor: "#4f46e5",
                color: "#ffffff",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#4338ca" },
              }}
            >
              Submit Feedback
            </Button>
          </>
        )
      )}
      {onDelete && (
        <>
          {!onEvaluate && <Divider sx={{ my: 0.5 }} />}
          <Button
            variant="outlined"
            size="small"
            fullWidth
            color="error"
            disabled={busy}
            onClick={handleDelete}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ textTransform: "none" }}
          >
            {deleting ? "Removing…" : "Remove Schedule"}
          </Button>
        </>
      )}
    </Box>
  );
};

// --- Needs Scheduling card ---
const SchedulingCard = ({ candidate, onScheduled, onError }) => {
  const [hovered, setHovered] = useState(false);
  const [touched, setTouched] = useState(false);
  const [inviting, setInviting] = useState(false);

  const sendAutomaticInvitation = async () => {
    setInviting(true);
    try {
      await API.post(`api/interview/${candidate.profile_id}/automatic-invitation`, {
        stage: candidate.detailed_status,
      });
      onScheduled(candidate.profile_id);
    } catch (error) {
      onError(error?.response?.data?.message || error?.message || "Failed to email candidate available slots.");
    } finally {
      setInviting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setTouched(true)}
      sx={{
        p: 1.5, mb: 1,
        border: "1px solid #E2E8F0",
        borderRadius: 2,
        transition: "box-shadow 0.15s",
        boxShadow: hovered || touched ? 2 : 0,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600}>
        {candidate.candidate_name}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
        <VideoCallIcon sx={{ fontSize: 14, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">
          {ROUND_LABELS[candidate.detailed_status] || candidate.detailed_status}
        </Typography>
      </Box>

      <Collapse in={hovered || touched}>
        <Box sx={{ mt: 1.25 }} onMouseDown={() => setTouched(true)}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={sendAutomaticInvitation}
            disabled={inviting}
            sx={{ textTransform: "none", mb: 1 }}
          >
            {inviting ? <CircularProgress size={14} /> : "Email Nylas slots to candidate"}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Or schedule directly using the existing admin controls:
          </Typography>
          <InterviewForm
            profileId={candidate.profile_id}
            onError={onError}
            onSave={(resolvedId, duration) => {
              setTouched(false);
              onScheduled(resolvedId, duration);
            }}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

// --- Calendar ---
// Lays out N same-cell, same-hour events side-by-side instead of stacked on top of one
// another. Event `index` of `total` gets left offset index/total and width 1/total. For
// total === 1 this reduces to exactly the original full-width, 2px-inset box, so
// single-event cells are pixel-identical to today's rendering.
export const getEventLayout = (index, total) => ({
  left: `calc(${index} * (100% / ${total}) + 2px)`,
  width: `calc((100% / ${total}) - 4px)`,
});

const CalendarView = ({ interviews, viewMode, currentDate, onEventClick, onEmptyCellClick }) => {
  const containerRef = useRef(null);
  const days = viewMode === "week" ? getWeekDays(currentDate) : [currentDate];
  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const now = new Date();

  // Reset scroll to business hours whenever the view mode changes (Day <-> Week),
  // but not on day/week navigation, so paging preserves the user's own scroll position.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const target = el.querySelector(`[data-hour="${BUSINESS_HOUR_START}"]`);
    if (target) el.scrollTop = target.offsetTop;
  }, [viewMode]);

  const getEventsForCell = (day, hour) =>
    interviews.filter((iv) => {
      const d = parseInterviewDateTime(iv.interview_datetime);
      return d && isSameDay(d, day) && d.getHours() === hour;
    });

  const isPastCell = (day, hour) => {
    const cellEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour + 1, 0, 0, 0);
    return cellEnd <= now;
  };

  const hasAnyEvent = interviews.some((iv) => {
    const d = parseInterviewDateTime(iv.interview_datetime);
    return d && days.some((day) => isSameDay(d, day));
  });

  return (
    <TableContainer
      ref={containerRef}
      component={Paper}
      elevation={0}
      sx={{ border: "1px solid #E2E8F0", borderRadius: 2, flex: 1, minHeight: 0, overflowY: "auto" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60, bgcolor: "#F8FAFC", fontWeight: 600 }} />
            {days.map((day, i) => (
              <TableCell
                key={i}
                align="center"
                sx={{
                  bgcolor: isSameDay(day, new Date()) ? "#EFF6FF" : "#F8FAFC",
                  fontWeight: isSameDay(day, new Date()) ? 700 : 500,
                  color: isSameDay(day, new Date()) ? "primary.main" : "text.primary",
                  fontSize: "0.75rem",
                }}
              >
                {viewMode === "week" ? DAY_LABELS[i] : "Today"}
                <br />
                <Typography variant="caption" color="text.secondary">
                  {day.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {HOUR_SLOTS.map((hour) => (
            <TableRow key={hour} data-hour={hour} sx={{ height: ROW_HEIGHT_PX }}>
              <TableCell sx={{ color: "text.secondary", fontSize: "0.72rem", verticalAlign: "top", pt: 0.5 }}>
                {formatHour(hour)}
              </TableCell>
              {days.map((day, i) => {
                const events = getEventsForCell(day, hour);
                const past = isPastCell(day, hour);
                const emptyAndClickable = events.length === 0 && !past;
                return (
                  <TableCell
                    key={i}
                    onClick={(e) => {
                      if (emptyAndClickable) onEmptyCellClick(day, hour, e.currentTarget);
                    }}
                    sx={{
                      p: 0.5,
                      verticalAlign: "top",
                      borderLeft: "1px solid #F1F5F9",
                      position: "relative",
                      ...(emptyAndClickable && {
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#F8FAFC" },
                      }),
                    }}
                  >
                    {events.map((ev, ei) => {
                      const parsedDt = parseInterviewDateTime(ev.interview_datetime);
                      const time = parsedDt
                        ? parsedDt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
                        : "";
                      const heightPx = Math.max(20, (ev.durationMinutes || DEFAULT_DURATION_MIN) * (ROW_HEIGHT_PX / 60));
                      const { left, width } = getEventLayout(ei, events.length);
                      return (
                        <Box
                          key={ev.profile_id ?? `${ev.interview_datetime}-${ei}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(ev, e.currentTarget);
                          }}
                          sx={{
                            position: "absolute",
                            top: 2, left, width,
                            height: heightPx,
                            zIndex: 1,
                            overflow: "hidden",
                            bgcolor: "#DBEAFE",
                            borderLeft: "3px solid #3B82F6",
                            borderRadius: "0 4px 4px 0",
                            px: 0.75, py: 0.25,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#BFDBFE" },
                          }}
                        >
                          <Typography variant="caption" noWrap display="block" fontWeight={600} color="#1D4ED8">
                            {ev.candidate_name}
                          </Typography>
                          <Typography variant="caption" noWrap display="block" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            {time}
                          </Typography>
                        </Box>
                      );
                    })}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {!hasAnyEvent && (
        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
          <CalendarMonthIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
              <Typography variant="body2">
            Interview slots are selected by candidates from the availability sent by email.
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

// --- Main component ---
const SchedulingAvailability = () => {
  const [scheduling, setScheduling] = useState([]);
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [loadingScheduling, setLoadingScheduling] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(true);
  const [viewMode, setViewMode] = useState("week");
  const [currentDate, setCurrentDate] = useState(new Date());

  // Popover state for calendar event editing/creation
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [evaluatingCandidate, setEvaluatingCandidate] = useState(null);

  // Cosmetic, client-only duration overrides keyed by profile_id — never persisted.
  // Kept in a ref (not state) so fetchCalendar always reads the latest value without
  // needing to be recreated / risking a stale closure.
  const durationOverridesRef = useRef({});

  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [alertType, setAlertType] = useState("");
  const handleFormError = (message) => {
    setAlertContent(message);
    setAlertType("error");
    setAlert(true);
  };

  const fetchScheduling = useCallback(async () => {
    try {
      setLoadingScheduling(true);
      const res = await API.get("api/interview/scheduling");
      setScheduling(res?.data || []);
    } catch {
      setScheduling([]);
    } finally {
      setLoadingScheduling(false);
    }
  }, []);

  const fetchCalendar = useCallback(async () => {
    try {
      setLoadingCalendar(true);
      const [legacyRes, candidateInterviewRes] = await Promise.all([
        API.get(
        "api/profile/all_candidate_profile?status=Interview_Scheduled&pageSize=100&page=1"
        ),
        API.get("api/interview/scheduled"),
      ]);
      const candidates = legacyRes?.data?.candidates || [];
      const candidateInterviews = Array.isArray(candidateInterviewRes?.data?.data)
        ? candidateInterviewRes.data.data
        : Array.isArray(candidateInterviewRes?.data)
        ? candidateInterviewRes.data
        : [];
      
      const combined = candidates
        .filter((c) => c.interview_datetime)
        .map((c) => ({
          profile_id: c.profile_id,
          candidate_name: c.candidate_name,
          interview_datetime: c.interview_datetime,
          interview_link: c.interview_link || "",
          detailed_status: c.detailed_status || "",
          candidate_detailed_status: c.detailed_status || "",
          interview_feedback: c.interview_feedback || "",
          feedback_submitted: Boolean(
            c.interview_feedback ||
            ["Selected_Onboarding", "Onboarding_In_Process", "Onboarded", "Staffed", "Rejected"].includes(c.detailed_status)
          ),
          evaluation_decision: ["Selected_Onboarding", "Onboarding_In_Process", "Onboarded", "Staffed"].includes(c.detailed_status)
            ? "passed"
            : c.detailed_status === "Rejected"
            ? "failed"
            : null,
          durationMinutes: durationOverridesRef.current[c.profile_id] ?? DEFAULT_DURATION_MIN,
        }))
        .concat(candidateInterviews);

      // Deduplicate by profile_id to prevent double rendering if both APIs return the same candidate
      const uniqueInterviews = Array.from(new Map(combined.map(item => [item.profile_id, item])).values());
      
      setScheduledInterviews(uniqueInterviews);
    } catch {
      setScheduledInterviews([]);
    } finally {
      setLoadingCalendar(false);
    }
  }, []);

  useEffect(() => {
    fetchScheduling();
    fetchCalendar();
  }, [fetchScheduling, fetchCalendar]);

  const handleScheduled = useCallback((profileId, duration) => {
    durationOverridesRef.current = { ...durationOverridesRef.current, [profileId]: duration };
    setScheduling((prev) => prev.filter((c) => c.profile_id !== profileId));
    fetchCalendar();
  }, [fetchCalendar]);

  const handleEventClick = useCallback((event, anchor) => {
    setSelectedEvent(event);
    setPopoverAnchor(anchor);
  }, []);

  const handleEmptyCellClick = useCallback((day, hour, anchor) => {
    const dateStr = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`;
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour >= 12 ? "PM" : "AM";
    // Synthesize the backend's own 'yyyy-MM-dd hh:mm a' shape so the existing
    // parsedEvent parser below needs zero changes for the create-on-click flow.
    setSelectedEvent({
      profile_id: null,
      candidate_name: null,
      interview_datetime: `${dateStr} ${pad2(hour12)}:00 ${ampm}`,
      interview_link: "",
      isNew: true,
    });
    setPopoverAnchor(anchor);
  }, []);

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setSelectedEvent(null);
  };

  // Pre-parse selected event's datetime into date + time strings for InterviewForm.
  const parsedEvent = selectedEvent
    ? (() => {
        const d = parseInterviewDateTime(selectedEvent.interview_datetime);
        if (d && isValid(d)) {
          const yyyy = d.getFullYear();
          const mm = pad2(d.getMonth() + 1);
          const dd = pad2(d.getDate());
          const hh = pad2(d.getHours());
          const min = pad2(d.getMinutes());
          return {
            initialDate: `${yyyy}-${mm}-${dd}`,
            initialTime: `${hh}:${min}`,
            initialLink: selectedEvent.interview_link || "",
          };
        }
        return {
          initialDate: "",
          initialTime: "",
          initialLink: selectedEvent.interview_link || "",
        };
      })()
    : null;

  const navigate = (dir) => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      viewMode === "week" ? d.setDate(d.getDate() + dir * 7) : d.setDate(d.getDate() + dir);
      return d;
    });
  };

  const dateLabel =
    viewMode === "week"
      ? (() => {
          const days = getWeekDays(currentDate);
          return `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
        })()
      : currentDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "stretch", height: "100%", minHeight: 0, flex: 1 }}>
      {/* Needs Scheduling panel */}
      <Box sx={{ width: 280, flexShrink: 0, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, p: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0, boxSizing: "border-box" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5, flexShrink: 0 }}>
            <CalendarMonthIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={600}>
              Needs Scheduling
            </Typography>
          </Box>

          {loadingScheduling ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              {scheduling.map((c) => (
                <SchedulingCard key={c.profile_id} candidate={c} onScheduled={handleScheduled} onError={handleFormError} />
              ))}
              {scheduling.length === 0 && (
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" py={2}>
                  All interviews scheduled.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Calendar panel */}
      <Box sx={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, p: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0, boxSizing: "border-box" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" onClick={() => navigate(-1)}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2" fontWeight={500}>
                {dateLabel}
              </Typography>
              <IconButton size="small" onClick={() => navigate(1)}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              onChange={(_, v) => v && setViewMode(v)}
              sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 2, py: 0.25, fontSize: "0.8rem" } }}
            >
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {loadingCalendar ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <CalendarView
              interviews={scheduledInterviews}
              viewMode={viewMode}
              currentDate={currentDate}
              onEventClick={handleEventClick}
              onEmptyCellClick={handleEmptyCellClick}
            />
          )}
        </Paper>
      </Box>

      {/* Edit / create popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, width: 270, borderRadius: 2 } }}
      >
        {selectedEvent && parsedEvent && (
          <>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
              {selectedEvent.isNew ? "Schedule New Interview" : selectedEvent.candidate_name}
            </Typography>
            <InterviewForm
              key={selectedEvent.profile_id ?? selectedEvent.interview_datetime}
              profileId={selectedEvent.profile_id}
              candidates={selectedEvent.isNew ? scheduling : null}
              initialDate={parsedEvent.initialDate}
              initialTime={parsedEvent.initialTime}
              initialLink={parsedEvent.initialLink}
              isFeedbackSubmitted={selectedEvent.feedback_submitted}
              evaluationDecision={selectedEvent.evaluation_decision}
              feedbackText={selectedEvent.interview_feedback}
              onError={handleFormError}
              onSave={(resolvedId, duration) => {
                durationOverridesRef.current = { ...durationOverridesRef.current, [resolvedId]: duration };
                handlePopoverClose();
                fetchCalendar();
                if (selectedEvent.isNew) fetchScheduling();
              }}
              onEvaluate={
                selectedEvent.isNew || !selectedEvent.profile_id
                  ? undefined
                  : () => {
                      const candidateToEvaluate = { ...selectedEvent };
                      handlePopoverClose();
                      setEvaluatingCandidate(candidateToEvaluate);
                    }
              }
              onDelete={selectedEvent.isNew ? undefined : () => {
                handlePopoverClose();
                fetchCalendar();
                fetchScheduling();
              }}
            />
          </>
        )}
      </Popover>

      {evaluatingCandidate && (
        <InterviewEvaluationModal
          open={Boolean(evaluatingCandidate)}
          candidate={evaluatingCandidate}
          onClose={() => setEvaluatingCandidate(null)}
          onSuccess={(result) => {
            setAlertContent(
              `Feedback submitted successfully (${result.decision === "passed" ? "Passed" : "Failed"}). Recruitment pipeline updated.`
            );
            setAlertType("success");
            setAlert(true);
            fetchCalendar();
            fetchScheduling();
          }}
        />
      )}

      <Notification
        message={alertContent}
        severity={alertType}
        open={alert}
        onClose={() => setAlert(false)}
      />
    </Box>
  );
};

export default SchedulingAvailability;
